import { getToken } from "../api";
import React, { useState, useEffect, useRef } from "react";

const authFetch = (url, opts = {}) => {
  const token = getToken();
  return fetch(url, { ...opts, headers: { ...(opts.headers || {}), ...(token ? { Authorization: `Bearer ${token}` } : {}) } });
};

const BASE = window.location.hostname === "localhost"
  ? "http://localhost:8001"
  : `${window.location.protocol}//${window.location.host}/api`;

export default function PrivateVault({ user }) {
  const [docs, setDocs]             = useState([]);
  const [uploading, setUploading]   = useState(false);
  const [uploadQueue, setUploadQueue] = useState([]);
  const [description, setDescription] = useState("");
  const [toast, setToast]           = useState("");
  const fileRef = useRef();

  const showToast = (msg) => { setToast(msg); setTimeout(() => setToast(""), 3000); };

  const loadDocs = async () => {
    try {
      const r = await authFetch(`${BASE}/vault/private-docs`);
      if (!r.ok) throw new Error();
      setDocs(await r.json());
    } catch { showToast("Error cargando documentos"); }
  };

  useEffect(() => { loadDocs(); }, []);

  const upload = async (e) => {
    const selected = Array.from(e.target.files);
    if (!selected.length) return;

    const queue = selected.map(f => ({ name: f.name, status: "pending" }));
    setUploadQueue(queue);
    setUploading(true);

    for (let i = 0; i < selected.length; i++) {
      const file = selected[i];
      const fd = new FormData();
      fd.append("file", file);
      fd.append("description", description);
      try {
        const r = await authFetch(`${BASE}/vault/private-docs/upload`, { method: "POST", body: fd });
        if (!r.ok) throw new Error();
        setUploadQueue(q => q.map((item, idx) => idx === i ? { ...item, status: "done" } : item));
      } catch {
        setUploadQueue(q => q.map((item, idx) => idx === i ? { ...item, status: "error" } : item));
      }
    }

    await loadDocs();
    setUploading(false);
    setDescription("");
    showToast(`${selected.length === 1 ? "Documento subido" : `${selected.length} documentos subidos`} ✓`);
    setTimeout(() => setUploadQueue([]), 2500);
    fileRef.current.value = "";
  };

  const deleteDoc = async (id, filename) => {
    if (!window.confirm(`¿Eliminar "${filename}"?`)) return;
    try {
      await authFetch(`${BASE}/vault/private-docs/${id}`, { method: "DELETE" });
      showToast("Documento eliminado");
      loadDocs();
    } catch { showToast("Error al eliminar"); }
  };

  const download = (id) => {
    const token = getToken();
    // Abrir en nueva pestaña con token en header no es posible directamente,
    // usamos fetch + blob para forzar descarga
    authFetch(`${BASE}/vault/private-docs/download/${id}`)
      .then(r => r.blob())
      .then(blob => {
        const doc = docs.find(d => d.id === id);
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url; a.download = doc?.filename || "documento";
        a.click(); URL.revokeObjectURL(url);
      })
      .catch(() => showToast("Error al descargar"));
  };

  const fmt     = (iso) => new Date(iso).toLocaleDateString("es-ES", { day: "2-digit", month: "short", year: "numeric" });
  const fmtSize = (b)   => b > 1048576 ? `${(b / 1048576).toFixed(1)} MB` : `${(b / 1024).toFixed(0)} KB`;

  const fileIcon = (ct) => {
    if (ct === "application/pdf") return "📄";
    if (ct?.startsWith("image/")) return "🖼";
    if (ct?.includes("word"))     return "📝";
    return "📎";
  };

  const S = {
    wrap:    { padding: 0, fontFamily: "Syne, sans-serif" },
    header:  { display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: "1.5rem", borderBottom: "0.5px solid var(--border)", paddingBottom: "1.5rem", flexWrap: "wrap", gap: 12 },
    title:   { fontSize: 26, fontWeight: 700, color: "var(--text)", fontFamily: "Syne, sans-serif", margin: 0, letterSpacing: "-0.5px" },
    sub:     { fontSize: 10, letterSpacing: "0.2em", textTransform: "uppercase", color: "var(--gold)", marginBottom: 8, fontFamily: "Syne, sans-serif" },
    btn:     { padding: "8px 14px", background: "var(--gold)", color: "var(--bgApp)", border: "none", fontFamily: "Syne, sans-serif", fontSize: 10, letterSpacing: "0.12em", textTransform: "uppercase", cursor: "pointer", fontWeight: 700, borderRadius: "5px" },
    input:   { background: "var(--lift)", border: "0.5px solid var(--border)", color: "var(--text)", padding: "9px 14px", fontFamily: "Syne, sans-serif", fontSize: 13, outline: "none", borderRadius: "6px", flex: 1 },
    table:   { width: "100%", borderCollapse: "collapse" },
    th:      { textAlign: "left", fontSize: 10, letterSpacing: "0.15em", textTransform: "uppercase", color: "var(--mute)", padding: "8px 12px", borderBottom: "0.5px solid var(--border)", fontFamily: "Syne, sans-serif" },
    td:      { padding: "12px", borderBottom: "0.5px solid var(--border)", color: "var(--text)", fontSize: 13, fontFamily: "Syne, sans-serif" },
    iconBtn: { background: "none", border: "none", cursor: "pointer", color: "var(--gold)", fontSize: 16, marginLeft: 8 },
    empty:   { textAlign: "center", padding: "3rem", color: "var(--mute)", fontSize: 12, letterSpacing: "0.1em", textTransform: "uppercase", fontFamily: "Syne, sans-serif" },
    toast:   { position: "fixed", bottom: 24, right: 24, background: "var(--card)", border: "1px solid var(--goldDim)", color: "var(--gold)", padding: "10px 20px", fontFamily: "Syne, sans-serif", fontSize: 12, fontWeight: 700, zIndex: 999, borderRadius: "6px" },
    label:   { fontSize: 10, letterSpacing: "0.15em", textTransform: "uppercase", color: "var(--mute)", marginBottom: 10, fontFamily: "Syne, sans-serif" },
    notice:  { display: "flex", alignItems: "center", gap: 10, background: "var(--lift)", border: "0.5px solid var(--goldDim)", borderRadius: 8, padding: "12px 16px", marginBottom: "1.5rem", fontSize: 12, color: "var(--mute)", fontFamily: "Syne, sans-serif" },
  };

  return (
    <div style={S.wrap}>
      {toast && <div style={S.toast}>{toast}</div>}

      <div style={S.header}>
        <div>
          <div style={S.sub}>Acceso restringido · Solo administradores</div>
          <h1 style={S.title}>🔒 Bóveda Privada</h1>
        </div>
      </div>

      <div style={S.notice}>
        <span style={{ fontSize: 18 }}>🔒</span>
        <span>Esta sección es privada y solo visible para administradores. Aquí puedes almacenar contratos, documentos de empleados y otros archivos sensibles.</span>
      </div>

      {/* Zona subida */}
      <div style={{ background: "var(--card)", border: "0.5px solid var(--border)", borderRadius: 8, padding: "16px", marginBottom: "1.5rem" }}>
        <div style={{ ...S.label, marginBottom: 12 }}>Subir documentos</div>
        <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginBottom: 8 }}>
          <input
            style={S.input}
            placeholder="Descripción (opcional, se aplica a todos)"
            value={description}
            onChange={e => setDescription(e.target.value)}
          />
          <input ref={fileRef} type="file" multiple style={{ display: "none" }} onChange={upload} />
          <button style={S.btn} onClick={() => fileRef.current.click()} disabled={uploading}>
            {uploading ? "Subiendo..." : "📎 Seleccionar ficheros"}
          </button>
        </div>
        <div style={{ fontSize: 11, color: "var(--mute)", fontFamily: "Syne, sans-serif" }}>
          Cualquier tipo de archivo · Puedes seleccionar varios a la vez
        </div>

        {/* Cola */}
        {uploadQueue.length > 0 && (
          <div style={{ marginTop: 12, display: "flex", flexDirection: "column", gap: 5 }}>
            {uploadQueue.map((item, i) => (
              <div key={i} style={{ display: "flex", alignItems: "center", gap: 10, fontSize: 12,
                fontFamily: "Syne, sans-serif", color: "var(--mute)" }}>
                <span style={{ fontSize: 14, flexShrink: 0 }}>
                  {item.status === "pending" ? "⏳" : item.status === "done" ? "✅" : "❌"}
                </span>
                <span style={{ flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                  {item.name}
                </span>
                <span style={{ color: item.status === "error" ? "#c0392b" : "var(--mute)", flexShrink: 0 }}>
                  {item.status === "pending" ? "Subiendo..." : item.status === "done" ? "Listo" : "Error"}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Lista documentos */}
      {docs.length === 0 ? (
        <div style={S.empty}>🔒 Sin documentos privados todavía</div>
      ) : (
        <table style={S.table}>
          <thead>
            <tr>
              <th style={S.th}>Archivo</th>
              <th style={S.th}>Descripción</th>
              <th style={S.th}>Tamaño</th>
              <th style={S.th}>Subido por</th>
              <th style={S.th}>Fecha</th>
              <th style={S.th}></th>
            </tr>
          </thead>
          <tbody>
            {docs.map(doc => (
              <tr key={doc.id}>
                <td style={S.td}>
                  <span style={{ marginRight: 8 }}>{fileIcon(doc.content_type)}</span>
                  {doc.filename}
                </td>
                <td style={{ ...S.td, color: "var(--mute)" }}>{doc.description || "—"}</td>
                <td style={{ ...S.td, color: "var(--mute)" }}>{fmtSize(doc.size)}</td>
                <td style={{ ...S.td, color: "var(--mute)" }}>{doc.uploaded_by}</td>
                <td style={{ ...S.td, color: "var(--mute)" }}>{fmt(doc.uploaded_at)}</td>
                <td style={S.td}>
                  <button style={S.iconBtn} onClick={() => download(doc.id)} title="Descargar">⬇</button>
                  <button style={{ ...S.iconBtn, color: "#c0392b" }} onClick={() => deleteDoc(doc.id, doc.filename)} title="Eliminar">✕</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}
